import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';

import { ExclamationCircleFilled } from '@ant-design/icons';
import { Empty, Modal, Table, Tooltip } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import Decimal from 'decimal.js';

import type { ActivePlan, Order, OrderStatus, UpgradablePlan } from '@/services/subscriptionService';
import { cancelSubscription, checkUpgrade, getActivePlans, getOrderList } from '@/services/subscriptionService';
import type { AccountInfo } from '@/services/usageService';

import styles from './UsageTab.module.less';
// import { MOCK_ULTRA_PLAN,MOCK_PRO_PLAN } from './mockData';
import { PlanCard } from './components';
import BillingIcon from './images/billing.svg?react';

interface OutletContextType {
  accountInfo: AccountInfo | null;
}

/** 订单状态枚举 → 圆点颜色映射（成功绿色，失败红色，其余蓝色进行中） */
 const STATUS_DOT_COLOR: Partial<Record<OrderStatus, string>> = {
  Paid: '#29E2CC', // 绿：支付成功
  Performed: '#29E2CC', // 绿：已履约
  Failed: '#E24E29', // 红：失败
  // 蓝色进行中（默认）：Submitted, PendingPayment, Canceled, Unknown
};

/** 根据订单状态枚举获取圆点颜色，未命中时默认蓝色 */
function getStatusDotColor(status: OrderStatus): string {
  return STATUS_DOT_COLOR[status] ?? '#29C3E2';
}

/** 格式化日期：中文 → 2024-03-11，英文 → Mar 11, 2024 */
function formatDate(timestamp: number, language: string): string {
  const isZh = language.startsWith('zh');
  return new Date(timestamp).toLocaleDateString(isZh ? 'zh-CN' : 'en-US', {
    year: 'numeric',
    month: isZh ? '2-digit' : 'short',
    day: '2-digit',
  });
}

/** 格式化日期+时间：中文 → 2024-03-11 14:30:00，英文 → Mar 11, 2024 14:30:00 */
function formatDateTime(timestamp: number, language: string): string {
  const date = new Date(timestamp);
  const pad = (n: number) => String(n).padStart(2, '0');
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  return `${formatDate(timestamp, language)} ${time}`;
}

export function PlanRelationTab() {
  const { accountInfo } = useOutletContext<OutletContextType>();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activePlans, setActivePlans] = useState<ActivePlan[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [upgradablePlans, setUpgradablePlans] = useState<UpgradablePlan[]>([]);

  // 当前订阅计划
  const fetchActivePlans = async () => {
    try {
      const plansRes = await getActivePlans();
      setActivePlans(plansRes.data?.length ? plansRes.data : []);
      // setActivePlans([MOCK_PRO_PLAN]);
    } catch (error) {
      console.error('Failed to fetch active plans:', error);
      setActivePlans([]);
    }
  };

  // 检查是否可以升级
  const fetchUpgradeCheck = async () => {
    try {
      const upgradeRes = await checkUpgrade();
      setUpgradablePlans(upgradeRes.data?.upgradablePlans || []);
    } catch (error) {
      console.error('Failed to check upgrade:', error);
      setUpgradablePlans([]);
    }
  };

  // 账单信息
  const fetchOrders = async (page: number, size: number) => {
    try {
      const ordersRes = await getOrderList({ page, pageSize: size });
      setOrders(ordersRes.data?.orders || []);
      setOrderTotal(ordersRes.data?.total || 0);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setOrders([]);
      setOrderTotal(0);
    }
  };

  // 标记是否是初次加载，避免分页 effect 与初始化请求重复
  const isInitializedRef = useRef(false);
  // 轮询定时器 ref，用于组件卸载时清理
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 获取订阅和账单信息（初始化）
  const fetchData = async (page: number, size: number) => {
    setLoading(true);
    await Promise.allSettled([fetchActivePlans(), fetchUpgradeCheck(), fetchOrders(page, size)]);
    setLoading(false);
  };

  useEffect(() => {
    if (!accountInfo) return;
    isInitializedRef.current = false;
    fetchData(currentPage, pageSize).then(() => {
      isInitializedRef.current = true;
    });
  }, [accountInfo?.account?.userId]);

  /**
   * 支付后轮询：检测到 URL 带有 Checkout.com 回调参数 cko-session-id 时启动。
   * 每 5s 轮询三个接口，直到组件卸载为止（return 清理）。
   * 启动后立即用 replace 清除 URL 参数，避免用户手动刷新页面再次触发轮询。
   */
  useEffect(() => {
    if (!searchParams.get('cko-session-id')) return;

    // 立即清除 URL 参数，避免刷新重复触发
    setSearchParams((prev) => {
      prev.delete('cko-session-id');
      return prev;
    }, { replace: true });

    const INTERVAL_MS = 5000;

    const poll = () => {
      Promise.allSettled([
        getActivePlans(),
        checkUpgrade(),
        getOrderList({ page: 1, pageSize: pageSize }),
      ]).then(([plansRes, upgradeRes, ordersRes]) => {
        if (plansRes.status === 'fulfilled') {
          setActivePlans(plansRes.value.data?.length ? plansRes.value.data : []);
        }
        if (upgradeRes.status === 'fulfilled') {
          setUpgradablePlans(upgradeRes.value.data?.upgradablePlans || []);
        }
        if (ordersRes.status === 'fulfilled') {
          setOrders(ordersRes.value.data?.orders || []);
          setOrderTotal(ordersRes.value.data?.total || 0);
        }
      }).catch(() => {
        // 静默处理，继续下一轮
      }).finally(() => {
        // 仍挂载则继续轮询
        pollTimerRef.current = setTimeout(poll, INTERVAL_MS);
      });
    };

    pollTimerRef.current = setTimeout(poll, INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  // 分页变化时重新获取订单（跳过初次加载）
  useEffect(() => {
    if (!isInitializedRef.current) return;
    fetchOrders(currentPage, pageSize);
  }, [currentPage, pageSize]);

  // 订单分页配置
  const orderPaginationConfig: TablePaginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    total: orderTotal,
    showSizeChanger: true,
    showTotal: (total) => t('usageTab.pagination.total', { total }),
    pageSizeOptions: ['10', '20', '50'],
    onChange: (page, size) => {
      setCurrentPage(page);
      setPageSize(size);
    },
  };

  // 升级计划
  const handleUpgradePlan = async (currentPlan: ActivePlan) => {
    console.log(currentPlan);
    navigate('/pricing');
  };

  // 取消续订
  const handleCancelRenew = () => {
    setCancelModalVisible(true);
  };

  // 取消续订弹窗（点击取消/关闭按钮）
  const handleModalCancel = () => {
    setCancelModalVisible(false);
  };

  // 确认取消续订（点击确认按钮）
  const handleModalConfirm = async () => {
    try {
      await cancelSubscription({ planCode: accountInfo?.account?.tierCode });
      await Promise.allSettled([fetchActivePlans(), fetchUpgradeCheck()]);
    } catch (error) {
      console.error('Failed to cancel renewal:', error);
    } finally {
      setCancelModalVisible(false);
    }
  };

  // 账单历史表格列配置
  const billingColumns: ColumnsType<Order> = [
    {
      title: t('usageTab.planTab.billing.colDate'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (timestamp: number) => (
        <Tooltip
          title={formatDateTime(timestamp, i18n.language)}
          color="#FFFFFF"
          placement="top"
          overlayInnerStyle={{
            fontSize: '14px',
            color: '#252626',
            borderRadius: '4px',
            padding: '5px 8px',
          }}
        >
          <span className="cursor-default">{formatDate(timestamp, i18n.language)}</span>
        </Tooltip>
      ),
    },
    {
      title: t('usageTab.planTab.billing.colProduct'),
      dataIndex: 'planName',
      key: 'planName',
    },
    {
      title: t('usageTab.planTab.billing.colAmount'),
      dataIndex: 'actualPrice',
      key: 'actualPrice',
      render: (price: number, record: Order) => {
        const amount = new Decimal(price).toFixed(2);
        return `${amount} ${record.currencyUnit || 'USD'}`;
      },
    },
    {
      title: t('usageTab.planTab.billing.colStatus'),
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      render: (_: OrderStatus, record: Order) => (
        <span className="inline-flex items-center gap-1.5">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="6"
            height="6"
            viewBox="0 0 6 6"
            fill="none"
          >
            <circle cx="3" cy="3" r="3" fill={getStatusDotColor(record.orderStatus)} />
          </svg>
          {t(`usageTab.orderStatus.${record.orderStatus}`, t('usageTab.orderStatus.Unknown'))}
        </span>
      ),
    },
    // TODO: 接口定义好后，替换 extraField 为真实字段名和列标题
    // {
    //   title: '账单',
    //   dataIndex: 'extraField',
    //   key: 'extraField',
    //   render: (val?: string) => val ?? '-',
    // },
  ];

  if (loading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-center py-12">
          <div className="size-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* 当前计划 */}
      <div className="flex flex-col gap-3">
        {activePlans.map((plan) => (
          <PlanCard
            key={plan.subscriptionId}
            plan={plan}
            upgradablePlans={upgradablePlans}
            onCancelRenew={handleCancelRenew}
            onUpgrade={handleUpgradePlan}
          />
        ))}
      </div>

      {/* 取消续订 */}
      <Modal
        open={cancelModalVisible}
        onOk={handleModalConfirm}
        onCancel={handleModalCancel}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: true, ghost: true }}
        width="90%"
        style={{ maxWidth: 400 }}
        closable
        rootClassName={styles.cancelRenewModal}
        styles={{ footer: { marginTop: 24 } }}
        title={
          <div className="flex items-center gap-2">
            <ExclamationCircleFilled className="text-[18px] text-[#FFAA00]" />
            <span>{t('usageTab.planTab.cancelModal.title')}</span>
          </div>
        }
      >
        <p className="m-0 text-sm leading-[22px] text-[#252626]">
          {t('usageTab.planTab.cancelModal.content')}
        </p>
      </Modal>

      {/* 订单历史 */}
      <div className="flex flex-col gap-4 rounded bg-white py-4">
        <div className="flex items-center gap-[6px]">
          <BillingIcon />
          <h3 className="text-base font-semibold tracking-[-0.16px] text-[#111314]">
            {t('usageTab.planTab.billing.title')}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <Table
            columns={billingColumns}
            dataSource={orders}
            rowKey="orderOutNo"
            size="small"
            className={styles.usageAuditTable}
            style={{ fontSize: '12px' }}
            pagination={orderPaginationConfig}
            locale={{
              emptyText: (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('common.noData')} />
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}
